import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { 
  Music, 
  Car, 
  RefreshCw, 
  Award,
  Play,
  Pause,
  CheckCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for bullets
const bulletIcons = [Car, RefreshCw, Award, Music, CheckCircle];

export default function GatedLandingPage() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sampleUrl, setSampleUrl] = useState(null);
  const [sampleInfo, setSampleInfo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const audioRef = React.useRef(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      // Don't send credentials for public endpoints - fixes Safari CORS issue
      const response = await axios.get(`${API}/api/landing-page-config`, { withCredentials: false });
      setConfig(response.data);
    } catch (error) {
      // Use defaults if fetch fails
      setConfig({
        headline: "A Remixed Way to Reinforce Black Letter Law",
        headline_highlight: "Remixed",
        subheadline: "Structured song-form drills designed to supplement traditional law school study.",
        bullets: [
          "Reinforce rules while commuting",
          "Review black letter law through repetition",
          "Designed by a 20+ year legal professional, songwriter, and current law student"
        ],
        button_text: "Get the Free Audio Sample",
        success_message: "You're on the list!",
        sample_description: "Here's your free sample. Enjoy!",
        footer_message: "We'll notify you when Bar Songz launches!"
      });
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      // Don't send credentials for waitlist - fixes Safari CORS issue
      const response = await axios.post(`${API}/api/waitlist`, { email }, { withCredentials: false });
      setSampleUrl(response.data.sample_url);
      setSampleInfo(response.data.sample_info);
      setIsSubmitted(true);
      toast.success("Success! Your free sample is ready to play.");
    } catch (error) {
      console.error("Waitlist error:", error);
      console.log("Error response:", error.response);
      console.log("Error response status:", error.response?.status);
      console.log("Error response data:", error.response?.data);
      
      if (error.response?.status === 409) {
        // Email already exists - still give them the sample
        const data = error.response.data;
        console.log("409 data:", data);
        setSampleUrl(data.sample_url);
        setSampleInfo(data.sample_info);
        setIsSubmitted(true);
        toast.info("Welcome back! Your sample is ready.");
      } else {
        toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  // Render headline with highlighted word
  const renderHeadline = () => {
    if (!config) return null;
    
    const { headline, headline_highlight } = config;
    if (!headline_highlight || !headline.includes(headline_highlight)) {
      return headline;
    }
    
    const parts = headline.split(headline_highlight);
    return (
      <>
        {parts[0]}
        <span className="text-gold italic" data-testid="headline-highlight">{headline_highlight}</span>
        {parts[1]}
      </>
    );
  };

  if (configLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="gated-landing-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <Music className="h-8 w-8 text-gold" />
            <span className="font-serif text-2xl font-bold">Bar Songz</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 pb-32">
        <div className="max-w-2xl w-full text-center space-y-8">
          {/* Headline */}
          <div className="space-y-4">
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" data-testid="landing-headline">
              {renderHeadline()}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground" data-testid="landing-subheadline">
              {config.subheadline}
            </p>
          </div>

          {/* Benefits */}
          <div className="flex flex-col items-center gap-4 max-w-2xl mx-auto px-4" data-testid="landing-bullets">
            {config.bullets.map((bullet, index) => {
              const Icon = bulletIcons[index % bulletIcons.length];
              return (
                <div key={index} className="flex flex-col items-center text-center" data-testid={`landing-bullet-${index}`}>
                  <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center mb-2">
                    <Icon className="h-5 w-5 text-gold" />
                  </div>
                  <p className="font-medium">{bullet}</p>
                </div>
              );
            })}
          </div>

          {/* Email Capture / Audio Player */}
          <Card className="p-6 md:p-8 bg-card border-border max-w-md mx-auto relative z-50">
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="text-center text-lg py-6"
                    disabled={isSubmitting}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gold text-primary-foreground hover:bg-gold/90 py-6 text-lg font-semibold"
                  disabled={isSubmitting}
                  data-testid="landing-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Please wait...
                    </>
                  ) : (
                    <>
                      <Music className="h-5 w-5 mr-2" />
                      {config.button_text}
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 text-green-500" data-testid="landing-success-message">
                  <CheckCircle className="h-6 w-6" />
                  <span className="font-medium">{config.success_message}</span>
                </div>
                
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground" data-testid="landing-sample-description">
                    {config.sample_description}
                  </p>
                  
                  {/* Audio Player */}
                  <div className="bg-background rounded-lg p-4 flex flex-col items-center gap-3">
                    <Button
                      size="sm"
                      className="bg-gold text-primary-foreground hover:bg-gold/90 w-12 h-12 rounded-full flex-shrink-0"
                      onClick={togglePlay}
                    >
                      {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                    </Button>
                    <div className="text-center w-full">
                      <p className="font-medium text-sm truncate">
                        {sampleInfo?.song_title || sampleInfo?.title || "Sample Track"}
                      </p>
                      {sampleInfo?.course_name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {sampleInfo.course_name}
                          {sampleInfo.title && ` • ${sampleInfo.title}`}
                        </p>
                      )}
                      {sampleInfo?.vibe && (
                        <p className="text-xs text-gold truncate capitalize">
                          {sampleInfo.vibe} Vibe
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {sampleUrl && (
                    <audio
                      ref={audioRef}
                      src={sampleUrl}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                      className="hidden"
                      controlsList="nodownload"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground pt-2" data-testid="landing-footer-message">
                  {config.footer_message}
                </p>
              </div>
            )}
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Bar Songz. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
